/**
 * "Example of XLS File Structure" on the file step (`FileSelection.tsx`): one sample row under the
 * column headers a file should have, required ones starred. Columns come from
 * `ExampleFileStructureTableConfig.tsx`; cart and quote uploads add the delivery-date column and
 * scroll horizontally.
 */
import React, { useMemo } from "react";
import { Flex } from "@ryerson/frontend.layout";
import { useApplication } from "@ryerson/frontend.application";
import styled from "@emotion/styled";
import { DataTable } from "@ryerson/frontend.table";
import { BulkUploadType } from "../BulkUpload.model";
import { css } from "@emotion/react";
import { getExampleFileStructureTableConfig } from "./ExampleFileStructureTableConfig";

const TableContainer = styled(Flex)`
	${(props: any) => {
		const { bulkUploadType } = props;
		if ([BulkUploadType.shoppingCart, BulkUploadType.quote].includes(bulkUploadType)) {
			return css`
				overflow: scroll;
			`;
		}
	}}
	thead tr td,
	thead tr td:first-of-type {
		height: 75px;
	}
`;

interface ExampleFileStructureTableProps {
	bulkUploadType: BulkUploadType;
}

// The sample row. Keys match the column ids in `ExampleFileStructureTableConfig.tsx`; the date
// cell shows the expected format rather than a date.
const exampleFileStructureTableDummyData = [
	{
		id: 0,
		partNumber: "304RT2X1X11",
		quantity: "100",
		quantityUom: "lbs",
		priceUom: "LB",
		customerRefNumber: "15235314",
		lineItemNotes: "example line item notes",
		lineLevelPO: "PO123",
		deliveryDate: "YYYY-MM-DD",
	},
];

/** The example-layout table; see the file header. */
const ExampleFileStructureTable: React.FC<ExampleFileStructureTableProps> = ({
	bulkUploadType,
}) => {
	const {
		localization: { language },
	} = useApplication();

	const fileSelectionTableConfig = useMemo(
		() => getExampleFileStructureTableConfig(language, bulkUploadType),
		[language, bulkUploadType]
	);

	return (
		<TableContainer alignItems="center" {...{ bulkUploadType }}>
			<DataTable
				config={fileSelectionTableConfig}
				data={exampleFileStructureTableDummyData}
				language={language}
				suppressMobileView={true}
				cardViewExpandRowPlacement="after"
				dataTestId="bulk-upload-modal-file-selection-table"
			/>
		</TableContainer>
	);
};

export default ExampleFileStructureTable;
